package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.ProducerDetail
import com.siriusxm.pia.views.unifiedaggregator.ProducersList
import com.siriusxm.pia.views.unifiedaggregator.navigate
import unifiedcontent.aggregator.Contact
import unifiedcontent.aggregator.ProducerDetails
import org.jetbrains.compose.web.dom.Text

/**
 * Editor composable for creating or editing a producer. Displays a form with fields for
 * producer properties including ID, name, client ID, contact info, and other metadata.
 *
 * Type permissions (base types and partials) are managed on separate dedicated editor screens
 * accessible from the producer detail page, not from this editor.
 *
 * The editor also sets the producer's permission model (LEGACY or UCP, catalog PR #710). The current
 * value is round-tripped on every save, so editing another field never changes it. Changing it opens
 * [PermissionModelChangeDialog], which requires the producer id to be typed.
 *
 * @param aggregator The aggregator service providing API access and navigation
 * @param producer The existing producer to edit, or null to create a new producer
 */
@Composable
fun producerEditor(aggregator: AggregatorService, producer: ProducerDetails? = null) {
    var updatedProducer by remember { mutableStateOf(producer ?: ProducerDetails("")) }
    var confirmingModelChange by remember { mutableStateOf(false) }

    dialogView(if (producer != null) "Edit Producer" else "Create Producer") {
        /**
         * Save the producer, returns true if successful.
         */
        suspend fun save(): Boolean {
            return validate {
                assertField(updatedProducer.id.length >= 5, "The id must be at least 5 characters")
                assertField(updatedProducer.name.orEmpty().length >= 5, "The name must be at least 5 characters")
                orElse {
                    try {
                        if (producer == null) {
                            aggregator.newProducer(updatedProducer)
                        } else {
                            aggregator.updateProducer(updatedProducer)
                        }
                    } catch (t: Throwable) {
                        errors.error("Unable to save producer: ${t.message}")
                    }
                }
            }.success
        }

        content {
            if (confirmingModelChange) {
                PermissionModelChangeDialog(
                    producerId = updatedProducer.id,
                    from = producer?.permissionModel,
                    to = updatedProducer.permissionModel.effective()
                ) { confirmed ->
                    // Save before closing: this callback runs in the dialog's coroutine scope, so
                    // removing the dialog first cancels the save before the request is sent.
                    if (confirmed && save()) {
                        aggregator.navigate(ProducerDetail(updatedProducer.id))
                    }
                    confirmingModelChange = false
                }
            }

            dialogField(
                "ID",
                if (producer == null) "Enter a unique id for the producer. The preferred format is `sxm/<workload-name>/<service-name>." else null
            ) {
                if (producer == null) {
                    simpleTextField(updatedProducer.id) {
                        updatedProducer = updatedProducer.copy(id = it ?: "")
                    }
                } else {
                    Text(updatedProducer.id)
                }
            }

            dialogField("Name", "A human readable name of the producer (i.e. Playback Services)") {
                simpleTextField(updatedProducer.name) {
                    updatedProducer = updatedProducer.copy(name = it)
                }
            }

            dialogField("Client ID", "The Cognito client id, for those producers that submit via API.") {
                simpleTextField(updatedProducer.clientId) {
                    updatedProducer = updatedProducer.copy(clientId = it ?: "")
                }
            }

            dialogField("Slack Channel", "The name of the Slack channel for support.") {
                simpleTextField(updatedProducer.slackChannel) {
                    updatedProducer = updatedProducer.copy(slackChannel = it ?: "")
                }
            }

            dialogField("JIRA Project Key", "The short key for the JIRA project for this producer.") {
                simpleTextField(updatedProducer.jiraProject) {
                    updatedProducer = updatedProducer.copy(jiraProject = it ?: "")
                }
            }

            dialogField("AWS Accounts", "The AWS accounts of this service.") {
                simpleTextField(updatedProducer.accounts?.joinToString(",")) {
                    updatedProducer = updatedProducer.copy(
                        accounts = it?.split(",")?.map { it.trim() }.orEmpty()
                    )
                }
            }

            dialogField("Contact Emails", "A comma separated list of emails of contacts for this producer.") {
                simpleTextField(updatedProducer.contact?.joinToString(",") { it.email }) {
                    updatedProducer = updatedProducer.copy(contact = it?.split(",")?.map { it.trim() }?.map {
                        Contact(it)
                    })
                }
            }

            dialogField("Service Registry ID", "The ID of this service in the Service Registry.") {
                simpleTextField(updatedProducer.serviceRegistryId) {
                    updatedProducer = updatedProducer.copy(serviceRegistryId = it)
                }
            }

            dialogField("UCP Team", "The id or friendly id of the UCP team that owns this producer. Members are managed in UCP.") {
                simpleTextField(updatedProducer.teamId) {
                    updatedProducer = updatedProducer.copy(teamId = it)
                }
            }

            dialogField(
                "Permission Model",
                updatedProducer.permissionModel.effective().description()
            ) {
                simpleSelect(
                    updatedProducer.permissionModel.effective(),
                    selectablePermissionModels.map { it.name to it }
                ) {
                    updatedProducer = updatedProducer.copy(permissionModel = it)
                }
            }

            dialogField("Feedback Target ARN", "An SQS queue or SNS topic ARN where invalid content update events will be routed for this producer.") {
                simpleTextField(updatedProducer.feedbackTargetArn) {
                    updatedProducer = updatedProducer.copy(feedbackTargetArn = it ?: "")
                }
            }
        }

        action {
            title = "Submit"
            primary = true
            showProgressOnAction = true
            this.action {
                if (permissionModelChangeNeedsConfirmation(producer?.permissionModel, updatedProducer.permissionModel)) {
                    confirmingModelChange = true
                } else if (save()) {
                    aggregator.navigate(ProducerDetail(updatedProducer.id))
                }
            }
        }
        action("Cancel") {
            aggregator.navigate(ProducersList)
        }
    }
}
